import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import ExpenseRequests from "@/components/expenses/ExpenseRequests";
import { inr, inrFull, fdate, ymd } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Plus, Upload, Download, Filter, X, Search, ChevronDown, Trash2, Pencil, ArrowLeftRight,
  TrendingUp, TrendingDown, Utensils, SprayCan, Coffee, Package, Lightbulb, Building2,
  Stethoscope, Car, Megaphone, Wrench, PawPrint, Receipt, Drumstick, Milk, Fuel, Pill, FileText,
} from "lucide-react";
import { CompareDrawer } from "@/components/pawkt/ExpenseCompare";

// Friendly, one-tap categories. `value` is the backend free-text category (backward-compatible);
// `type` is a safe default expense_type (existing taxonomy) so staff never pick accounting terms.
const CATEGORIES = [
  { value: "food", label: "Food", icon: Utensils, type: "DIRECT_SERVICE_COST" },
  { value: "cleaning", label: "Cleaning", icon: SprayCan, type: "MAINTENANCE" },
  { value: "staff", label: "Staff", icon: Coffee, type: "STAFF_WELFARE" },
  { value: "supplies", label: "Supplies", icon: Package, type: "OPERATING_EXPENSE" },
  { value: "utilities", label: "Utilities", icon: Lightbulb, type: "UTILITIES" },
  { value: "rent", label: "Rent", icon: Building2, type: "RENT" },
  { value: "medical", label: "Medical", icon: Stethoscope, type: "DIRECT_SERVICE_COST" },
  { value: "travel", label: "Travel", icon: Car, type: "TRANSPORT" },
  { value: "marketing", label: "Marketing", icon: Megaphone, type: "MARKETING" },
  { value: "repairs", label: "Repairs", icon: Wrench, type: "MAINTENANCE" },
  { value: "pet_care", label: "Pet Care", icon: PawPrint, type: "DIRECT_SERVICE_COST" },
  { value: "other", label: "Other", icon: Receipt, type: "OTHER" },
  { value: "salaries", label: "Salaries", icon: Wallet, type: "OPERATING_EXPENSE" },
];

const SHORTCUTS = [
  { icon: Drumstick, label: "Chicken", category: "food", subcategory: "Chicken", type: "DIRECT_SERVICE_COST" },
  { icon: Coffee, label: "Tea & Refreshments", category: "staff", subcategory: "Tea & Refreshments", type: "STAFF_WELFARE" },
  { icon: Milk, label: "Milk", category: "food", subcategory: "Milk", type: "DIRECT_SERVICE_COST" },
  { icon: PawPrint, label: "Pet Food", category: "supplies", subcategory: "Pet Food", type: "OPERATING_EXPENSE" },
  { icon: SprayCan, label: "Cleaning Materials", category: "cleaning", subcategory: "Cleaning Materials", type: "MAINTENANCE" },
  { icon: FileText, label: "Stationery", category: "supplies", subcategory: "Stationery", type: "ADMINISTRATION" },
  { icon: Fuel, label: "Fuel", category: "travel", subcategory: "Fuel", type: "TRANSPORT" },
  { icon: Pill, label: "Medicines", category: "medical", subcategory: "Medicines", type: "DIRECT_SERVICE_COST" },
  { icon: Wrench, label: "Repairs", category: "repairs", subcategory: "Repairs", type: "MAINTENANCE" },
];

const PAY_METHODS = ["Cash", "UPI", "Bank Transfer", "Card", "Cheque", "Other"];
const EXPENSE_TYPES = [
  ["DIRECT_SERVICE_COST", "Direct Service Cost"], ["OPERATING_EXPENSE", "Operating Expense"],
  ["INVENTORY_CONSUMPTION", "Inventory / Consumables"], ["STAFF_WELFARE", "Staff Welfare"],
  ["UTILITIES", "Utilities"], ["MAINTENANCE", "Maintenance"], ["MARKETING", "Marketing"],
  ["ADMINISTRATION", "Administration"], ["FINANCIAL_COST", "Financial Cost"],
  ["SOFTWARE_SUBSCRIPTION", "Software / Subscription"], ["RENT", "Rent"],
  ["PROFESSIONAL_FEE", "Professional Fee"], ["TRANSPORT", "Transport"],
  ["CAPITAL_EXPENDITURE", "Capital Expenditure (CAPEX)"], ["OTHER", "Other"],
].map(([value, label]) => ({ value, label }));

const GENERAL = "shared_overhead";
const OVERHEAD_DEPTS = ["shared_overhead", "administration", "overhead", "other"];
const areaLabel = (d) => (!d ? "Unallocated" : d === GENERAL ? "Shared overhead" : prettyCat(d));
const today = () => ymd();
const monthRange = (offset = 0) => {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const last = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
  const iso = (x) => ymd(x);
  return { from: iso(first), to: iso(last), label: first.toLocaleString("en-US", { month: "long" }) };
};
const prettyCat = (c) => (c || "other").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || prettyCat(v);
const typeLabel = (t) => EXPENSE_TYPES.find((o) => o.value === t)?.label || "";

const EMPTY_FILTERS = { q: "", category: "all", department_id: "all", payment_method: "all", vendor: "", expense_type: "all", is_capex: "all", recurring: "all", from: "", to: "" };
const LIMIT = 25;

// Hydrate the register filters from URL query params (used by Comparison deep-links).
const initFromParams = (sp) => {
  const g = (k, d) => sp.get(k) ?? d;
  return {
    q: g("q", ""), category: g("category", "all"), department_id: g("department_id", "all"),
    payment_method: g("payment_method", "all"), vendor: g("vendor", ""),
    expense_type: g("expense_type", "all"), is_capex: g("is_capex", "all"),
    recurring: g("recurring", "all"), from: g("date_from", ""), to: g("date_to", ""),
  };
};

// Human-readable context for the comparison deep-link banner (no IDs, only present params).
const buildCompareBanner = (sp) => {
  if ([...sp.keys()].length === 0) return null;
  const chips = [];
  const df = sp.get("date_from"), dt = sp.get("date_to");
  if (df || dt) chips.push({ id: "date", label: `${df ? fdate(df) : "…"} – ${dt ? fdate(dt) : "…"}`, remove: ["date_from", "date_to"] });
  if (sp.get("category")) chips.push({ id: "category", label: catLabel(sp.get("category")), remove: ["category"] });
  if (sp.get("department_id")) chips.push({ id: "department", label: prettyCat(sp.get("department_id")), remove: ["department_id"] });
  if (sp.get("payment_method")) chips.push({ id: "payment", label: sp.get("payment_method"), remove: ["payment_method"] });
  if (sp.get("vendor")) chips.push({ id: "vendor", label: `Vendor: ${sp.get("vendor")}`, remove: ["vendor"] });
  if (sp.get("recurring")) chips.push({ id: "recurring", label: sp.get("recurring") === "true" ? "Recurring" : "One-off", remove: ["recurring"] });
  const ic = sp.get("is_capex"), et = sp.get("expense_type");
  const cls = ic === "true" ? "CAPEX" : et === "FINANCIAL_COST" ? "Financial Cost" : ic === "false" ? "Operating" : "";
  if (cls) chips.push({ id: "classification", label: cls, remove: et === "FINANCIAL_COST" ? ["expense_type", "is_capex"] : ["is_capex"] });
  return chips.length ? { chips } : null;
};

export default function Expenses() {
  const { user } = useAuth();
  return (
    <div>
      {!hasPerm(user, "expenses.view") && <PageHeader title="Expenses" subtitle="Raise expense payment requests for Accounts." />}
      <ExpenseRequests />
      {hasPerm(user, "expenses.view") && <ExpensesRegister />}
    </div>
  );
}

function ExpensesRegister() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasPerm(user, "expenses.edit");
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [searchParams] = useSearchParams();
  const [f, setF] = useState(() => initFromParams(searchParams));
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareBanner, setCompareBanner] = useState(() => buildCompareBanner(searchParams));

  // When navigated to with filter query params (Comparison deep-link), apply them + reveal filters + show context banner.
  useEffect(() => {
    setF(initFromParams(searchParams));
    setCompareBanner(buildCompareBanner(searchParams));
    setPage(0);
    if ([...searchParams.keys()].length > 0) {
      setShowFilters(true);
      setTimeout(() => document.getElementById("exp-register")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const thisM = useMemo(() => monthRange(0), []);
  const prevM = useMemo(() => monthRange(-1), []);

  const { data: deptData } = useQuery({ queryKey: ["expense-departments"], queryFn: async () => (await api.get("/expense-departments")).data });
  const { data: services } = useQuery({ queryKey: ["services-min"], queryFn: async () => (await api.get("/services")).data });
  const { data: sumAll } = useQuery({ queryKey: ["exp-sum-all"], queryFn: async () => (await api.get("/finance/summary")).data });
  const { data: sumThis } = useQuery({ queryKey: ["exp-sum-this"], queryFn: async () => (await api.get(`/finance/summary?date_from=${thisM.from}&date_to=${thisM.to}`)).data });
  const { data: sumPrev } = useQuery({ queryKey: ["exp-sum-prev"], queryFn: async () => (await api.get(`/finance/summary?date_from=${prevM.from}&date_to=${prevM.to}`)).data });

  const departments = deptData?.departments || [];
  const deptFilterOpts = [{ value: "__unallocated__", label: "Unallocated" }, ...departments.map((d) => ({ value: d, label: prettyCat(d) }))];
  const svcOpts = (services || []).map((s) => ({ value: s.id, label: s.name }));
  const svcName = useMemo(() => Object.fromEntries((services || []).map((s) => [s.id, s.name])), [services]);
  // Include the active category (e.g. a deep-linked value outside the 12 preset tiles) so the Select renders it.
  const categoryOpts = useMemo(() => {
    const base = CATEGORIES.map((c) => ({ value: c.value, label: c.label }));
    if (f.category !== "all" && !base.some((o) => o.value === f.category)) base.unshift({ value: f.category, label: prettyCat(f.category) });
    return base;
  }, [f.category]);

  const qp = useMemo(() => {
    const p = new URLSearchParams();
    if (f.q) p.set("q", f.q);
    if (f.category !== "all") p.set("category", f.category);
    if (f.department_id !== "all") p.set("department_id", f.department_id === "__unallocated__" ? "" : f.department_id);
    if (f.payment_method !== "all") p.set("payment_method", f.payment_method);
    if (f.vendor) p.set("vendor", f.vendor);
    if (f.expense_type !== "all") p.set("expense_type", f.expense_type);
    if (f.is_capex !== "all") p.set("is_capex", f.is_capex);
    if (f.recurring !== "all") p.set("recurring", f.recurring);
    if (f.from) p.set("date_from", f.from);
    if (f.to) p.set("date_to", f.to);
    return p.toString();
  }, [f]);

  // Base filters shared with the comparison drawer (CAPEX governed there by expense_class).
  const compareFilters = useMemo(() => ({
    category: f.category !== "all" ? f.category : "",
    department_id: f.department_id !== "all" ? (f.department_id === "__unallocated__" ? "" : f.department_id) : "",
    payment_method: f.payment_method !== "all" ? f.payment_method : "",
    vendor: f.vendor || "",
    expense_type: f.expense_type !== "all" ? f.expense_type : "",
    recurring: f.recurring !== "all" ? f.recurring : "",
    q: f.q || "",
  }), [f]);

  const { data: list, isLoading } = useQuery({
    queryKey: ["exp-list", qp, page],
    queryFn: async () => (await api.get(`/expenses?limit=${LIMIT}&skip=${page * LIMIT}&${qp}`)).data,
    keepPreviousData: true,
  });
  const rows = list?.items || [];
  const total = list?.total || 0;

  const refresh = () => {
    ["exp-list", "exp-sum-all", "exp-sum-this", "exp-sum-prev"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const createExp = useMutation({ mutationFn: (v) => api.post("/expenses", v), onError: (e) => toast.error(apiErr(e)) });
  const updateExp = useMutation({ mutationFn: ({ id, v }) => api.put(`/expenses/${id}`, v), onError: (e) => toast.error(apiErr(e)) });
  const deleteExp = useMutation({ mutationFn: (id) => api.delete(`/expenses/${id}`), onSuccess: () => { refresh(); setDetail(null); toast.success("Expense deleted"); }, onError: (e) => toast.error(apiErr(e)) });

  const changePct = useMemo(() => {
    const t = sumThis?.operating_expenses_total || 0, p = sumPrev?.operating_expenses_total || 0;
    if (!p) return null;
    return ((t - p) / p) * 100;
  }, [sumThis, sumPrev]);

  const activeFilters = Object.entries(f).filter(([, v]) => v && v !== "all").length;
  const resetFilters = () => { setF(EMPTY_FILTERS); setPage(0); setCompareBanner(null); };
  const setFilter = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(0); setCompareBanner(null); };
  // Clear the comparison deep-link context: drop query params + reset to the default register.
  const clearCompareContext = () => { setCompareBanner(null); setF(EMPTY_FILTERS); setPage(0); setShowFilters(false); navigate("/expenses"); };
  // Remove ONE comparison chip = delete only its URL param(s); the effect re-applies filters + rebuilds chips.
  const removeChip = (removeKeys) => {
    const sp = new URLSearchParams(searchParams);
    removeKeys.forEach((k) => sp.delete(k));
    const qs = sp.toString();
    navigate(qs ? `/expenses?${qs}` : "/expenses");
  };

  const drillCategory = (cat) => { setF({ ...EMPTY_FILTERS, category: cat, from: thisM.from, to: thisM.to }); setPage(0); setShowFilters(true); window.scrollTo({ top: document.getElementById("exp-register")?.offsetTop || 0, behavior: "smooth" }); };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/expenses/export?${qp}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "expenses.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const breakdown = (sumThis?.expenses_by_category || []).filter((c) => c.amount > 0).sort((a, b) => b.amount - a.amount);

  const cards = [
    { label: "Total Expenses", value: sumAll?.operating_expenses_total, tid: "exp-card-total", icon: Wallet },
    { label: `${thisM.label} (This Month)`, value: sumThis?.operating_expenses_total, tid: "exp-card-this" },
    { label: `${prevM.label} (Previous)`, value: sumPrev?.operating_expenses_total, tid: "exp-card-prev" },
  ];

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Track every business expense in one place.">
        <Button variant="outline" onClick={() => navigate("/expense-import")} data-testid="exp-import-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Import Expenses</Button>
        <Button variant="outline" onClick={() => setCompareOpen(true)} data-testid="exp-compare-btn" className="gap-1.5"><ArrowLeftRight className="h-4 w-4" /> Compare</Button>
        {canEdit && <Button onClick={() => { setEditRow(null); setAddOpen(true); }} data-testid="exp-add-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> Add Expense</Button>}
      </PageHeader>

      <CompareDrawer open={compareOpen} onClose={() => setCompareOpen(false)} baseFilters={compareFilters} departments={departments} />

      {compareBanner && (
        <div data-testid="exp-compare-banner" className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5"><ArrowLeftRight className="h-4 w-4 shrink-0" /> Showing expenses for your comparison</div>
            <Button variant="ghost" size="sm" onClick={clearCompareContext} data-testid="exp-compare-banner-clear" className="shrink-0 text-indigo-700 hover:text-indigo-900">Clear all</Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="exp-compare-chips">
            {compareBanner.chips.map((chip) => (
              <span key={chip.id} data-testid={`exp-compare-chip-${chip.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-white pl-3 pr-1 py-1 text-xs font-medium text-indigo-900">
                {chip.label}
                <button type="button" onClick={() => removeChip(chip.remove)} aria-label={`Remove ${chip.label} filter`} data-testid={`exp-compare-chip-remove-${chip.id}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700 transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Monthly summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="exp-summary">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid={c.tid}>
            <div className="text-xs text-slate-500">{c.label}</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-slate-900">{inr(c.value || 0)}</div>
          </div>
        ))}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid="exp-card-change">
          <div className="text-xs text-slate-500">Change vs {prevM.label}</div>
          <div className={`mt-1 font-display text-2xl font-extrabold flex items-center gap-1.5 ${changePct == null ? "text-slate-400" : changePct > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {changePct == null ? "—" : (<>{changePct > 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}{Math.abs(changePct).toFixed(1)}%</>)}
          </div>
        </div>
      </div>

      {/* Category breakdown for this month */}
      {breakdown.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="exp-breakdown">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-3">{thisM.label} by category <span className="text-slate-400 font-normal text-sm">· tap to view</span></h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {breakdown.map((c) => (
              <button key={c.category} onClick={() => drillCategory(c.category)} data-testid={`exp-breakdown-${c.category}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5 text-left hover:border-slate-400 hover:bg-slate-50 transition-colors">
                <span className="text-sm font-medium text-slate-700 capitalize truncate">{prettyCat(c.category)}</span>
                <span className="text-sm font-bold text-slate-900 shrink-0 ml-2">{inr(c.amount)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Register */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" id="exp-register" data-testid="exp-register">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="exp-search" value={f.q} onChange={(e) => setFilter("q", e.target.value)} placeholder="Search description, vendor…" className="h-9 pl-8 bg-white" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="exp-toggle-filters" className="gap-1.5"><Filter className="h-4 w-4" /> Filters{activeFilters ? ` (${activeFilters})` : ""}</Button>
          {activeFilters > 0 && <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="exp-clear-filters" className="gap-1 text-slate-500"><X className="h-3.5 w-3.5" /> Clear</Button>}
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="exp-export-btn" className="gap-1.5"><Download className="h-4 w-4" /> Export CSV</Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="exp-filter-panel">
            <Fld label="From"><Input type="date" data-testid="exp-filter-from" value={f.from} onChange={(e) => setFilter("from", e.target.value)} className="h-9 bg-white" /></Fld>
            <Fld label="To"><Input type="date" data-testid="exp-filter-to" value={f.to} onChange={(e) => setFilter("to", e.target.value)} className="h-9 bg-white" /></Fld>
            <FSel label="Category" value={f.category} onChange={(v) => setFilter("category", v)} options={categoryOpts} tid="exp-filter-category" />
            <FSel label="Business Area" value={f.department_id} onChange={(v) => setFilter("department_id", v)} options={deptFilterOpts} tid="exp-filter-department" />
            <FSel label="Payment" value={f.payment_method} onChange={(v) => setFilter("payment_method", v)} options={PAY_METHODS.map((m) => ({ value: m, label: m }))} tid="exp-filter-payment" />
            <FSel label="Expense Type" value={f.expense_type} onChange={(v) => setFilter("expense_type", v)} options={EXPENSE_TYPES} tid="exp-filter-type" />
            <FSel label="CAPEX" value={f.is_capex} onChange={(v) => setFilter("is_capex", v)} options={[{ value: "true", label: "CAPEX" }, { value: "false", label: "Operating" }]} tid="exp-filter-capex" />
            <FSel label="Recurring" value={f.recurring} onChange={(v) => setFilter("recurring", v)} options={[{ value: "true", label: "Recurring" }, { value: "false", label: "One-off" }]} tid="exp-filter-recurring" />
            <Fld label="Vendor"><Input data-testid="exp-filter-vendor" value={f.vendor} onChange={(e) => setFilter("vendor", e.target.value)} placeholder="Vendor" className="h-9 bg-white" /></Fld>
          </div>
        )}

        {isLoading ? <Loading /> : rows.length === 0 ? (
          <Empty icon={Wallet} title="No expenses" subtitle={activeFilters ? "No expenses match these filters." : "Add your first expense to start tracking."} />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-testid="exp-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3">Business Area</th><th className="py-2 pr-3">Vendor</th><th className="py-2 pr-3">Payment</th><th className="py-2 pr-3 text-right">Amount</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((e) => (
                    <tr key={e.id} onClick={() => setDetail(e)} data-testid={`exp-row-${e.id}`} className="cursor-pointer hover:bg-slate-50">
                      <td className="py-2.5 pr-3 whitespace-nowrap text-slate-600">{fdate(e.expense_date)}</td>
                      <td className="py-2.5 pr-3"><span className="font-medium text-slate-800">{catLabel(e.category)}</span>{e.is_capex && <Tag c="indigo">CAPEX</Tag>}{e.recurring && <Tag c="slate">Recurring</Tag>}</td>
                      <td className="py-2.5 pr-3 text-slate-600 max-w-[220px] truncate">{e.subcategory || e.description || "—"}</td>
                      <td className="py-2.5 pr-3 capitalize text-slate-600" data-testid={`exp-row-area-${e.id}`}>{areaLabel(e.department_id)}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{e.vendor || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{e.payment_method || "—"}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-900">{inr((e.amount || 0) + (e.tax || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100" data-testid="exp-cards">
              {rows.map((e) => (
                <button key={e.id} onClick={() => setDetail(e)} data-testid={`exp-card-${e.id}`} className="w-full flex items-center gap-3 py-3 text-left">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800">{catLabel(e.category)}{e.is_capex && <Tag c="indigo">CAPEX</Tag>}</div>
                    <div className="text-xs text-slate-500 truncate">{e.subcategory || e.description || "—"}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{fdate(e.expense_date)} · {areaLabel(e.department_id)}</div>
                  </div>
                  <div className="font-semibold text-slate-900 shrink-0">{inr((e.amount || 0) + (e.tax || 0))}</div>
                </button>
              ))}
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-sm text-slate-500" data-testid="exp-pagination">
              <span>{page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="exp-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage((p) => p + 1)} data-testid="exp-next-page">Next</Button>
              </div>
            </div>
          </>
        )}
      </div>

      {(addOpen || editRow) && (
        <AddExpenseModal
          open={addOpen || !!editRow}
          onClose={() => { setAddOpen(false); setEditRow(null); }}
          initial={editRow}
          canEdit={canEdit}
          departments={departments}
          svcOpts={svcOpts}
          onSubmit={(v) => (editRow ? updateExp.mutateAsync({ id: editRow.id, v }) : createExp.mutateAsync(v))}
          onComplete={() => { refresh(); setAddOpen(false); setEditRow(null); setDetail(null); }}
          busy={createExp.isPending || updateExp.isPending}
        />
      )}

      <ExpenseDetail
        row={detail}
        onClose={() => setDetail(null)}
        svcName={svcName}
        canEdit={canEdit}
        onEdit={() => { setEditRow(detail); setDetail(null); }}
        onDelete={() => deleteExp.mutate(detail.id)}
        deleting={deleteExp.isPending}
      />
    </div>
  );
}

const Fld = ({ label, children }) => (<div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</div>{children}</div>);
const FSel = ({ label, value, onChange, options, tid }) => (
  <Fld label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="h-9 bg-white"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white max-h-64"><SelectItem value="all">All</SelectItem>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </Fld>
);
const Tag = ({ c, children }) => (<span className={`ml-1.5 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${c === "indigo" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{children}</span>);

export function ExpenseAttachments({ eid, canEdit, base = "/expenses" }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["exp-att", eid], queryFn: async () => (await api.get(`${base}/${eid}/attachments`)).data });
  const items = data?.items || [];
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post(`${base}/${eid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); qc.invalidateQueries({ queryKey: ["exp-att", eid] }); toast.success("Bill attached"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const view = async (a) => {
    try { const r = await api.get(`${base}/${eid}/attachments/${a.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank"); } catch (err) { toast.error(apiErr(err)); }
  };
  const remove = async (a) => {
    try { await api.delete(`${base}/${eid}/attachments/${a.id}`); qc.invalidateQueries({ queryKey: ["exp-att", eid] }); toast.success("Removed"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="exp-attachments">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">Bills / Receipts</span>
        {canEdit && <label data-testid="exp-att-add" className="cursor-pointer text-xs font-medium text-slate-900 hover:underline">
          {busy ? "Uploading…" : "+ Add bill"}
          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={busy} />
        </label>}
      </div>
      {items.length === 0 ? <p className="text-xs text-slate-400">No bills attached</p> : (
        <ul className="space-y-1">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-xs" data-testid={`exp-att-${a.id}`}>
              <button onClick={() => view(a)} className="flex-1 truncate text-left text-slate-700 hover:underline" data-testid={`exp-att-view-${a.id}`}>{a.label || a.mime_type}</button>
              {canEdit && <button onClick={() => remove(a)} className="text-rose-600 hover:text-rose-700" data-testid={`exp-att-del-${a.id}`}>Remove</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ATT_OK = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const ATT_MAX = 8 * 1024 * 1024;

export function AddExpenseModal({ open, onClose, initial, canEdit, departments, svcOpts, onSubmit, onComplete, busy, request }) {
  const editing = !!initial;
  const lastPay = (typeof window !== "undefined" && localStorage.getItem("pawkt_last_pay")) || "Cash";
  const [v, setV] = useState(() => initial ? {
    ...initial,
    department_id: initial.department_id || "",
    expense_date: initial.expense_date || today(),
    payment_method: initial.payment_method || "Cash",
  } : {
    category: "", subcategory: "", amount: "", expense_date: today(), payment_method: lastPay,
    department_id: "", expense_type: "", vendor: "", description: "", quantity: "", unit: "",
    unit_cost: "", tax: "", reference_no: "", recurring: false, is_capex: false, notes: "", service_id: "",
  });
  const [more, setMore] = useState(false);
  const [staged, setStaged] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const pickCategory = (c) => setV((s) => ({ ...s, category: c.value, expense_type: s.expense_type || c.type }));
  const pickShortcut = (sc) => setV((s) => ({ ...s, category: sc.category, subcategory: sc.subcategory, expense_type: sc.type }));

  // PAWKT-EXPENSE-MULTI-ITEM — optional itemised lines (each with a quantity). When any item
  // exists the top amount becomes the server-computed subtotal (qty x unit price).
  const [items, setItems] = useState(() => (initial?.items || []).map((it) => ({
    description: it.description || "", quantity: it.quantity ?? 1, unit_price: it.unit_price ?? it.unit_cost ?? "" })));
  const useItems = items.length > 0;
  const itemsSubtotal = Math.round(items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0) * 100) / 100;
  const addItem = () => setItems((s) => [...s, { description: "", quantity: 1, unit_price: "" }]);
  const updItem = (i, k, val) => setItems((s) => s.map((it, idx) => (idx === i ? { ...it, [k]: val } : it)));
  const rmItem = (i) => setItems((s) => s.filter((_, idx) => idx !== i));

  const addStaged = (e) => {
    const files = Array.from(e.target.files || []);
    const good = [];
    for (const f of files) {
      if (!ATT_OK.includes(f.type)) { toast.error(`${f.name}: unsupported file type`); continue; }
      if (f.size > ATT_MAX) { toast.error(`${f.name}: exceeds 8MB`); continue; }
      good.push(f);
    }
    if (good.length) setStaged((s) => [...s, ...good]);
    e.target.value = "";
  };
  const removeStaged = (i) => setStaged((s) => s.filter((_, idx) => idx !== i));

  const staffQ = useQuery({ queryKey: ["staff", ""], retry: false, queryFn: async () => (await api.get("/staff")).data });
  const serviceAreas = departments.filter((d) => !OVERHEAD_DEPTS.includes(d));
  const submit = async () => {
    if (!v.category) return toast.error("Pick a category");
    if (!v.department_id) return toast.error("Select the service / department (or Shared overhead)");
    const cleanItems = items
      .map((it) => ({ description: it.description || "", quantity: Number(it.quantity), unit_price: Number(it.unit_price || 0) }))
      .filter((it) => it.description || it.quantity || it.unit_price);
    if (useItems) {
      if (!cleanItems.length || cleanItems.some((it) => !(it.quantity > 0))) return toast.error("Each item needs a quantity greater than 0");
      if (cleanItems.some((it) => it.unit_price < 0)) return toast.error("Item unit price cannot be negative");
    } else if (!v.amount || Number(v.amount) <= 0) {
      return toast.error("Enter an amount");
    }
    const payload = { ...v };
    ["quantity", "unit_cost", "tax", "amount"].forEach((k) => { if (payload[k] === "") delete payload[k]; else payload[k] = Number(payload[k]); });
    if (useItems) { payload.items = cleanItems; delete payload.amount; }
    if (!editing) localStorage.setItem("pawkt_last_pay", v.payment_method);
    setSaving(true);
    try {
      const res = await onSubmit(payload);
      const id = editing ? initial.id : res?.data?.id;
      if (!editing && id && staged.length) {
        for (const f of staged) {
          const fd = new FormData(); fd.append("file", f);
          try { await api.post(`${request ? "/expense-requests" : "/expenses"}/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
          catch (err) { toast.error(`${f.name}: ${apiErr(err)}`); }
        }
      }
      toast.success(request ? "Draft saved" : editing ? "Expense updated" : "Expense recorded");
      onComplete();
    } catch (_e) { setSaving(false); /* onError toast shown by caller */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="exp-add-modal">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{request ? (editing ? `Edit request ${initial.number}` : "New expense request") : editing ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
        {request && <div className="mb-1" data-testid="exr-vendor-field"><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Vendor / payee to be paid (registered)</div>
          <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={v.vendor_id || ""} onChange={(e) => set("vendor_id", e.target.value)} data-testid="exr-vendor">
            <option value="">Choose vendor…</option>{(request.vendors || []).map((x) => <option key={x.id} value={x.id}>{x.legal_name}</option>)}</select></div>}

        {!editing && (
          <div data-testid="exp-shortcuts" className="min-w-0">
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">Quick add</Label>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {SHORTCUTS.map((sc) => (
                <button key={sc.label} onClick={() => pickShortcut(sc)} data-testid={`exp-shortcut-${sc.category}-${sc.subcategory}`}
                  className={`shrink-0 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${v.subcategory === sc.subcategory && v.category === sc.category ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  <sc.icon className="h-3.5 w-3.5" /> {sc.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-1">
          <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">What did you spend on?</Label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="exp-category-grid">
            {CATEGORIES.map((c) => {
              const active = v.category === c.value;
              return (
                <button key={c.value} onClick={() => pickCategory(c)} data-testid={`exp-cat-${c.value}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50"}`}>
                  <c.icon className="h-5 w-5" /><span className="text-[11px] font-medium">{c.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="col-span-2">
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">What did you buy?</Label>
            <Input data-testid="exp-subcategory" value={v.subcategory || ""} onChange={(e) => set("subcategory", e.target.value)} placeholder="e.g. Chicken for boarding" className="bg-white" />
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">How much? (₹)</Label>
            <Input data-testid="exp-amount" type="number" inputMode="decimal" value={useItems ? itemsSubtotal : (v.amount ?? "")} disabled={useItems} onChange={(e) => set("amount", e.target.value)} placeholder="0" className="bg-white text-lg font-semibold disabled:opacity-70" />
            {useItems && <p className="text-[10px] text-slate-400 mt-0.5">Auto-calculated from items below</p>}
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">When?</Label>
            <Input data-testid="exp-date" type="date" value={v.expense_date} onChange={(e) => set("expense_date", e.target.value)} className="bg-white" />
          </div>
        </div>

        <div className="mt-2" data-testid="exp-items-section">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Items & quantity (optional)</Label>
            <button type="button" onClick={addItem} data-testid="exp-add-item" className="text-xs font-semibold text-slate-900 hover:underline">+ Add Item</button>
          </div>
          {useItems ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_52px_76px_70px_24px] gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 px-0.5">
                <span>Item</span><span className="text-center">Qty</span><span>₹/unit</span><span className="text-right">Total</span><span />
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_52px_76px_70px_24px] gap-1.5 items-center" data-testid={`exp-item-${i}`}>
                  <Input placeholder="Item" value={it.description} onChange={(e) => updItem(i, "description", e.target.value)} data-testid={`exp-item-desc-${i}`} className="bg-white h-9" />
                  <Input type="number" inputMode="decimal" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} data-testid={`exp-item-qty-${i}`} className="bg-white h-9 text-center px-1" />
                  <Input type="number" inputMode="decimal" value={it.unit_price} onChange={(e) => updItem(i, "unit_price", e.target.value)} data-testid={`exp-item-price-${i}`} className="bg-white h-9 px-1" />
                  <span className="text-xs font-medium text-slate-700 text-right tabular" data-testid={`exp-item-total-${i}`}>{inrFull((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</span>
                  <button type="button" onClick={() => rmItem(i)} data-testid={`exp-item-del-${i}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-slate-100">
                <span className="text-slate-500">Subtotal</span><span data-testid="exp-items-subtotal">{inrFull(itemsSubtotal)}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400">Add multiple line items (e.g. Dog Food × 5, Shampoo × 10) in one expense.</p>
          )}
        </div>

        <div className="mt-1">
          <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">Paid by?</Label>
          <div className="flex flex-wrap gap-2" data-testid="exp-pay-group">
            {["Cash", "UPI", "Bank Transfer", "Card"].map((m) => (
              <button key={m} onClick={() => set("payment_method", m)} data-testid={`exp-pay-${m.replace(/\s/g, "-")}`}
                className={`rounded-lg border px-3.5 py-1.5 text-sm font-medium transition-colors ${v.payment_method === m ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{m}</button>
            ))}
          </div>
        </div>

        <div className="mt-1">
          <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5 block">Service / Department <span className="text-rose-500">*</span></Label>
          <div className="flex flex-wrap gap-2" data-testid="exp-area-group">
            {serviceAreas.map((d) => (
              <AreaBtn key={d} active={v.department_id === d} onClick={() => set("department_id", d)} tid={`exp-area-${d}`}>{prettyCat(d)}</AreaBtn>
            ))}
            <AreaBtn active={v.department_id === GENERAL} onClick={() => set("department_id", GENERAL)} tid="exp-area-shared-overhead">Shared overhead</AreaBtn>
          </div>
          {!v.department_id && <p className="text-[11px] text-amber-700 mt-1" data-testid="exp-area-required">Required: which service is this cost for? Use Shared overhead for rent, utilities, marketing.</p>}
        </div>

        {v.category === "salaries" && (
          <div className="mt-1" data-testid="exp-salary-staff">
            <AdvSel label="Salary paid to (links to staff salary, avoids double counting)" value={v.staff_id || ""} onChange={(x) => set("staff_id", x)}
              options={(staffQ.data || []).map((s) => ({ value: s.id, label: s.name }))} tid="exp-salary-staff-select" />
            {!v.staff_id && <p className="text-[11px] text-amber-700 mt-1">Unlinked salary expenses are flagged for review in Service Profitability.</p>}
          </div>
        )}

        <button onClick={() => setMore((x) => !x)} data-testid="exp-more-toggle" className="mt-2 flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Hide details" : "+ More details"}
        </button>

        {more && (
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 mt-1" data-testid="exp-advanced">
            <AdvSel label="Expense Type" value={v.expense_type} onChange={(x) => set("expense_type", x)} options={EXPENSE_TYPES} tid="exp-adv-type" />
            <AdvSel label="Service (optional)" value={v.service_id} onChange={(x) => set("service_id", x)} options={svcOpts} tid="exp-adv-service" />
            <AdvInp label="Vendor" value={v.vendor} onChange={(x) => set("vendor", x)} tid="exp-adv-vendor" />
            <AdvInp label="Reference No." value={v.reference_no} onChange={(x) => set("reference_no", x)} tid="exp-adv-ref" />
            <AdvInp label="Quantity" type="number" value={v.quantity} onChange={(x) => set("quantity", x)} tid="exp-adv-qty" />
            <AdvInp label="Unit (kg, box…)" value={v.unit} onChange={(x) => set("unit", x)} tid="exp-adv-unit" />
            <AdvInp label="Unit Cost (₹)" type="number" value={v.unit_cost} onChange={(x) => set("unit_cost", x)} tid="exp-adv-unitcost" />
            <AdvInp label="Tax (₹)" type="number" value={v.tax} onChange={(x) => set("tax", x)} tid="exp-adv-tax" />
            <div className="col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Description</Label>
              <Input data-testid="exp-adv-desc" value={v.description || ""} onChange={(e) => set("description", e.target.value)} className="bg-white" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Notes</Label>
              <Textarea data-testid="exp-adv-notes" value={v.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-white" />
            </div>
            <div className="col-span-2 flex items-center gap-6 pt-1">
              <Chk testid="exp-adv-recurring" checked={v.recurring} onChange={(c) => set("recurring", c)} label="Recurring" />
              <Chk testid="exp-adv-capex" checked={v.is_capex} onChange={(c) => set("is_capex", c)} label="Capital expenditure (CAPEX)" />
            </div>
          </div>
        )}

        {editing ? (
          <ExpenseAttachments eid={initial.id} base={request ? "/expense-requests" : "/expenses"} canEdit={canEdit && initial.status !== "void"} />
        ) : (
          <div className="mt-3 border-t border-slate-100 pt-3" data-testid="exp-attachments-staged">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">Bills / Receipts</span>
              <label data-testid="exp-att-stage-add" className="cursor-pointer text-xs font-medium text-slate-900 hover:underline">
                + Upload Bill
                <input type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={addStaged} />
              </label>
            </div>
            {staged.length === 0 ? (
              <p className="text-xs text-slate-400">Attach one or more bills/receipts (optional). They’ll be saved with the expense.</p>
            ) : (
              <ul className="space-y-1">
                {staged.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs" data-testid={`exp-att-staged-${i}`}>
                    <span className="flex-1 truncate text-slate-700">{f.name}</span>
                    <button type="button" onClick={() => removeStaged(i)} className="text-rose-600 hover:text-rose-700" data-testid={`exp-att-staged-del-${i}`}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || saving} data-testid="exp-save-btn" className="bg-slate-900 text-white">{(busy || saving) ? "Saving…" : request ? "Save draft" : editing ? "Save Changes" : "Save Expense"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AreaBtn = ({ active, onClick, tid, children }) => (
  <button onClick={onClick} data-testid={tid} className={`rounded-lg border px-3.5 py-1.5 text-sm font-medium capitalize transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{children}</button>
);
const AdvInp = ({ label, value, onChange, type, tid }) => (
  <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">{label}</Label>
    <Input data-testid={tid} type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 bg-white" /></div>
);
const AdvSel = ({ label, value, onChange, options, tid }) => (
  <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="h-9 bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white max-h-64">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select></div>
);
const Chk = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-slate-900" />
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

function ExpenseDetail({ row, onClose, svcName, canEdit, onEdit, onDelete, deleting }) {
  const [confirm, setConfirm] = useState(false);
  if (!row) return null;
  const detail = (l, val) => val ? <div key={l} className="flex justify-between py-1.5 border-b border-slate-100 text-sm"><span className="text-slate-500">{l}</span><span className="font-medium text-slate-800 text-right">{val}</span></div> : null;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md max-h-[88vh] overflow-y-auto" data-testid="exp-detail-modal">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{catLabel(row.category)}</DialogTitle></DialogHeader>
        <div className="font-display text-3xl font-extrabold text-slate-900">{inrFull((row.amount || 0) + (row.tax || 0))}</div>
        {Array.isArray(row.items) && row.items.length > 0 && (
          <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 p-2 space-y-0.5" data-testid="exp-detail-items">
            {row.items.map((it, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span className="text-slate-600 truncate">{it.description || "Item"} × {it.quantity}</span>
                <span className="font-medium text-slate-800">{inrFull(it.line_total ?? (Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2">
          {detail("Category", catLabel(row.category))}
          {detail("What", row.subcategory)}
          {detail("Description", row.description)}
          {detail("Date", fdate(row.expense_date))}
          {detail("Service / Department", areaLabel(row.department_id))}
          {detail("Payment Method", row.payment_method)}
          {detail("Vendor", row.vendor)}
          {detail("Service", row.service_id ? svcName[row.service_id] : "")}
          {detail("Quantity", row.quantity ? `${row.quantity}${row.unit ? " " + row.unit : ""}` : "")}
          {detail("Unit Cost", row.unit_cost ? inrFull(row.unit_cost) : "")}
          {detail("Tax", row.tax ? inrFull(row.tax) : "")}
          {detail("Reference", row.reference_no)}
          {detail("Expense Type", typeLabel(row.expense_type))}
          {detail("Recurring", row.recurring ? "Yes" : "")}
          {detail("CAPEX", row.is_capex ? "Yes" : "")}
          {detail("Source", row.source)}
          {detail("Notes", row.notes)}
        </div>
        <ExpenseAttachments eid={row.id} canEdit={canEdit} />
        {canEdit && (
          <DialogFooter className="mt-2">
            {confirm ? (
              <div className="w-full rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="exp-delete-confirm">
                <p className="text-sm font-semibold text-rose-800">Delete this expense?</p>
                <p className="mt-0.5 text-xs text-rose-600">{(row.vendor || catLabel(row.category))} · {fdate(row.expense_date)} · {inrFull((row.amount || 0) + (row.tax || 0))}</p>
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" onClick={() => setConfirm(false)} disabled={deleting} data-testid="exp-delete-cancel">Cancel</Button>
                  <Button onClick={onDelete} disabled={deleting} data-testid="exp-delete-confirm-btn" className="bg-rose-600 text-white hover:bg-rose-700">{deleting ? "Deleting…" : "Delete"}</Button>
                </div>
              </div>
            ) : (
              <>
                <Button variant="outline" onClick={() => setConfirm(true)} disabled={deleting} data-testid="exp-detail-delete" className="gap-1.5 text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /> Delete</Button>
                <Button onClick={onEdit} data-testid="exp-detail-edit" className="gap-1.5 bg-slate-900 text-white"><Pencil className="h-4 w-4" /> Edit</Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
